import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"

export default class extends Controller {
  static values = {
    url: String,
    days: { type: Number, default: 7 },
    categoryColors: { type: Object, default: {} },
    categoryNames: { type: Object, default: {} }
  }
  static targets = ["preview"]

  connect() {
    this.selectedTopicId = null
    this.loadGraph()
    this.resizeHandler = this.debounce(() => this.handleResize(), 250)
    window.addEventListener('resize', this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener('resize', this.resizeHandler)
    if (this.simulation) {
      this.simulation.stop()
    }
  }

  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  handleResize() {
    if (this.data && this.data.nodes.length > 0) {
      this.renderGraph(this.data)
    }
  }

  async loadGraph() {
    try {
      const response = await fetch(this.urlValue)
      const data = await response.json()
      this.data = data
      this.renderGraph(data)
    } catch (error) {
      console.error("Failed to load graph data:", error)
      document.getElementById("loading").innerHTML =
        '<div class="text-red-500 text-sm">読み込みに失敗しました</div>'
    }
  }

  renderGraph(data) {
    const container = document.getElementById("graph-container")
    const svgElement = document.getElementById("topic-graph")
    const loading = document.getElementById("loading")

    if (data.nodes.length === 0) {
      loading.innerHTML = '<div class="text-gray-400 text-sm text-center">データがありません</div>'
      return
    }

    loading.classList.add("hidden")
    svgElement.classList.remove("hidden")

    const svg = d3.select("#topic-graph")
    svg.selectAll("*").remove()

    const width = container.clientWidth - 16
    const height = container.clientHeight - 16

    svg.attr("width", width).attr("height", height)

    // Defs for visual effects
    const defs = svg.append("defs")

    // Glow filter
    const filter = defs.append("filter")
      .attr("id", "glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%")

    filter.append("feGaussianBlur")
      .attr("stdDeviation", "2")
      .attr("result", "coloredBlur")

    const feMerge = filter.append("feMerge")
    feMerge.append("feMergeNode").attr("in", "coloredBlur")
    feMerge.append("feMergeNode").attr("in", "SourceGraphic")

    // Selected glow
    const selectedFilter = defs.append("filter")
      .attr("id", "selected-glow")
      .attr("x", "-100%")
      .attr("y", "-100%")
      .attr("width", "300%")
      .attr("height", "300%")

    selectedFilter.append("feGaussianBlur")
      .attr("stdDeviation", "4")
      .attr("result", "coloredBlur")

    const selectedMerge = selectedFilter.append("feMerge")
    selectedMerge.append("feMergeNode").attr("in", "coloredBlur")
    selectedMerge.append("feMergeNode").attr("in", "SourceGraphic")

    // Color scale
    const colorScale = d3.scaleOrdinal()
      .domain(Object.keys(this.categoryColorsValue))
      .range(Object.values(this.categoryColorsValue))
      .unknown("#9ca3af")

    // Size scale
    const maxSize = d3.max(data.nodes, d => d.size) || 10
    const sizeScale = d3.scaleSqrt()
      .domain([1, maxSize])
      .range([12, 40])

    // Link width scale
    const maxWeight = d3.max(data.links, d => d.weight) || 1
    const linkWidthScale = d3.scaleLinear()
      .domain([1, maxWeight])
      .range([1, 5])

    // Simulation
    this.simulation = d3.forceSimulation(data.nodes)
      .force("link", d3.forceLink(data.links)
        .id(d => d.id)
        .distance(70)
        .strength(0.7))
      .force("charge", d3.forceManyBody()
        .strength(-150)
        .distanceMax(180))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide()
        .radius(d => sizeScale(d.size) + 6)
        .strength(0.9))
      .force("x", d3.forceX(width / 2).strength(0.04))
      .force("y", d3.forceY(height / 2).strength(0.04))

    // Links
    const link = svg.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(data.links)
      .enter()
      .append("line")
      .attr("stroke", "#9ca3af")
      .attr("stroke-opacity", 0.25)
      .attr("stroke-width", d => linkWidthScale(d.weight))

    // Nodes
    const node = svg.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(data.nodes)
      .enter()
      .append("g")
      .attr("cursor", "pointer")
      .call(d3.drag()
        .on("start", (event, d) => this.dragstarted(event, d))
        .on("drag", (event, d) => this.dragged(event, d))
        .on("end", (event, d) => this.dragended(event, d)))
      .on("click", (event, d) => {
        event.preventDefault()
        event.stopPropagation()
        this.selectTopic(d, node, link, colorScale, sizeScale)
      })
      .on("mouseenter", (event, d) => this.handleHover(event, d, true, link, node))
      .on("mouseleave", (event, d) => this.handleHover(event, d, false, link, node))

    // Selection ring
    node.append("circle")
      .attr("class", "selection-ring")
      .attr("r", d => sizeScale(d.size) + 5)
      .attr("fill", "none")
      .attr("stroke", d => colorScale(d.category))
      .attr("stroke-width", 0)
      .attr("stroke-opacity", 0.6)

    // Main circle
    node.append("circle")
      .attr("class", "main-circle")
      .attr("r", d => sizeScale(d.size))
      .attr("fill", d => colorScale(d.category))
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .style("filter", "url(#glow)")

    // Article count
    node.append("text")
      .attr("class", "count-label")
      .text(d => d.size)
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("fill", "#fff")
      .attr("font-size", d => Math.max(10, sizeScale(d.size) / 2.2))
      .attr("font-weight", "bold")
      .attr("pointer-events", "none")

    // Topic name
    node.append("text")
      .attr("class", "name-label")
      .text(d => d.name.length > 6 ? d.name.substring(0, 6) + "…" : d.name)
      .attr("text-anchor", "middle")
      .attr("dy", d => sizeScale(d.size) + 14)
      .attr("fill", "#374151")
      .attr("font-size", "11")
      .attr("font-weight", "600")
      .attr("pointer-events", "none")

    // Tick
    this.simulation.on("tick", () => {
      data.nodes.forEach(d => {
        const r = sizeScale(d.size)
        d.x = Math.max(r + 10, Math.min(width - r - 10, d.x))
        d.y = Math.max(r + 10, Math.min(height - r - 25, d.y))
      })

      link
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y)

      node.attr("transform", d => `translate(${d.x},${d.y})`)
    })

    this.simulation.alpha(0.8).alphaDecay(0.025)

    // Store references
    this.node = node
    this.link = link
    this.colorScale = colorScale
    this.sizeScale = sizeScale
  }

  async selectTopic(d, node, link, colorScale, sizeScale) {
    // Update visual selection
    node.select(".selection-ring")
      .attr("stroke-width", n => n.id === d.id ? 4 : 0)

    node.select(".main-circle")
      .style("filter", n => n.id === d.id ? "url(#selected-glow)" : "url(#glow)")

    this.selectedTopicId = d.id

    // Show preview panel
    const previewPanel = document.getElementById("topic-preview-panel")
    const previewContent = document.getElementById("topic-preview-content")

    if (previewPanel && previewContent) {
      // Show loading state
      previewPanel.classList.remove("hidden")
      previewContent.innerHTML = `
        <div class="animate-pulse space-y-3">
          <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
        </div>
      `

      try {
        const response = await fetch(`/news/topics/${d.id}/preview?days=${this.daysValue}`)
        const data = await response.json()
        this.renderPreview(data, previewContent, colorScale)
      } catch (error) {
        console.error("Failed to load preview:", error)
        previewContent.innerHTML = `
          <div class="text-red-500 text-sm">読み込みに失敗しました</div>
        `
      }
    }
  }

  renderPreview(data, container, colorScale) {
    const catColor = this.categoryColorsValue[data.topic.category] || "#9ca3af"

    let articlesHtml = ''
    if (data.articles.length > 0) {
      articlesHtml = data.articles.map(article => `
        <a href="${article.url}" target="_blank" rel="noopener noreferrer"
           class="block py-2 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800 -mx-2 px-2 transition">
          <div class="font-medium text-gray-900 dark:text-white text-sm leading-snug">
            ${article.title}
          </div>
          <div class="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
            <span>${article.media_name}</span>
            <span>${article.published_at}</span>
          </div>
        </a>
      `).join('')
    } else {
      articlesHtml = `
        <div class="text-gray-400 dark:text-gray-500 text-sm py-4 text-center">
          この期間の記事はありません
        </div>
      `
    }

    container.innerHTML = `
      <div class="flex items-center justify-between mb-3">
        <h4 class="font-bold text-gray-900 dark:text-white text-lg leading-tight">
          ${data.topic.name}
        </h4>
        <span class="text-white text-xs font-bold px-2 py-0.5 rounded" style="background-color: ${catColor}">
          ${data.articles.length}件
        </span>
      </div>
      <div class="space-y-1">
        ${articlesHtml}
      </div>
      <a href="/news/topics/${data.topic.id}"
         class="block mt-3 text-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
        すべて見る →
      </a>
    `
  }

  handleHover(event, d, isHovering, link, node) {
    const connectedNodeIds = new Set()

    if (isHovering) {
      link.each(function(l) {
        if (l.source.id === d.id) connectedNodeIds.add(l.target.id)
        if (l.target.id === d.id) connectedNodeIds.add(l.source.id)
      })
      connectedNodeIds.add(d.id)

      link
        .attr("stroke-opacity", l =>
          (l.source.id === d.id || l.target.id === d.id) ? 0.7 : 0.1)
        .attr("stroke", l =>
          (l.source.id === d.id || l.target.id === d.id) ? "#3b82f6" : "#9ca3af")

      node.select(".main-circle")
        .attr("opacity", n => connectedNodeIds.has(n.id) ? 1 : 0.35)

      node.select(".name-label")
        .attr("opacity", n => connectedNodeIds.has(n.id) ? 1 : 0.35)

      node.select(".count-label")
        .attr("opacity", n => connectedNodeIds.has(n.id) ? 1 : 0.35)
    } else {
      link
        .attr("stroke-opacity", 0.25)
        .attr("stroke", "#9ca3af")

      node.select(".main-circle")
        .attr("opacity", 1)

      node.select(".name-label")
        .attr("opacity", 1)

      node.select(".count-label")
        .attr("opacity", 1)
    }
  }

  dragstarted(event, d) {
    if (!event.active) this.simulation.alphaTarget(0.3).restart()
    d.fx = d.x
    d.fy = d.y
  }

  dragged(event, d) {
    d.fx = event.x
    d.fy = event.y
  }

  dragended(event, d) {
    if (!event.active) this.simulation.alphaTarget(0)
    d.fx = null
    d.fy = null
  }
}
